'use client';

import React, { ReactNode } from 'react';
import styles from '@/styles/system-development-pages.module.scss';

interface Section {
  title: string;
  content: ReactNode;
  className?: string;
}

interface FlowStep {
  step: string;
  title: string;
  description: string;
  duration: string;
}

interface GridItem {
  icon?: string;
  title: string;
  description: string;
  highlight?: string;
  items?: string[];
}

interface ServicePageContentProps {
  sections?: Section[];
  flowSteps?: FlowStep[];
  gridItems?: {
    title?: string;
    columns?: 2 | 3 | 4;
    items: GridItem[];
  }[];
  customContent?: ReactNode;
}

export function ServicePageContent({
  sections,
  flowSteps,
  gridItems,
  customContent
}: ServicePageContentProps) {
  return (
    <div className={styles.pageContent}>
      {sections && sections.map((section, index) => (
        <section key={index} className={`${styles.section} ${section.className || ''}`}>
          <h2 className={styles.sectionTitle}>{section.title}</h2>
          <div className={styles.content}>
            {section.content}
          </div>
        </section>
      ))}

      {flowSteps && flowSteps.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>開発フロー</h2>
          <div className={styles.flowContainer}>
            {flowSteps.map((item, index) => (
              <div key={index} className={styles.flowStep}>
                <div className={styles.flowNumber}>
                  {item.step}
                </div>
                <h3 className={styles.flowTitle}>{item.title}</h3>
                <p className={styles.flowDescription}>{item.description}</p>
                <span className={styles.flowDuration}>
                  {item.duration}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {gridItems && gridItems.map((grid, gridIndex) => (
        <section key={gridIndex} className={styles.section}>
          {grid.title && <h2 className={styles.sectionTitle}>{grid.title}</h2>}
          <div className={`${styles.grid} ${styles[`cols${grid.columns || 3}`]}`}>
            {grid.items.map((item, index) => (
              <div key={index} className={styles.card}>
                {item.icon && <div className={styles.cardIcon}>{item.icon}</div>}
                <h3 className={styles.cardTitle}>{item.title}</h3>
                <p className={styles.cardDescription}>{item.description}</p>
                {item.items && (
                  <ul>
                    {item.items.map((listItem, i) => (
                      <li key={i}>{listItem}</li>
                    ))}
                  </ul>
                )}
                {item.highlight && (
                  <p className={styles.cardHighlight}>{item.highlight}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      {customContent}
    </div>
  );
}

// 便利なコンポーネント
export function FeatureBox({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.featureBox}>
      <h3 className={styles.subtitle}>{title}</h3>
      <div className={styles.content}>
        {children}
      </div>
    </div>
  );
}

export function AchievementSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.achievementSection}>
      <h3 className={styles.subtitle}>{title}</h3>
      {children}
    </div>
  );
}

export function CTASection({
  title,
  description,
  buttonText,
  buttonHref
}: {
  title: string;
  description: string;
  buttonText: string;
  buttonHref: string;
}) {
  return (
    <div className={styles.ctaSection}>
      <h2 className={styles.ctaTitle}>{title}</h2>
      <p className={styles.ctaDescription}>{description}</p>
      <a href={buttonHref} className={styles.ctaButton}>
        {buttonText}
      </a>
    </div>
  );
}